<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import PhoneNumberFormattedContent from './PhoneNumberFormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import FacebookPostContext from 'next/message/bubbles/FacebookPostContext.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import LocationMap from '../LocationMap.vue';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

// Extract agent name prefix if present
const agentNamePrefix = computed(() => {
  const contentText = content.value || '';
  const match = contentText.match(/^\*\*([^*]+):\*\*\n/);
  return match ? match[1] : null;
});

const contentWithoutPrefix = computed(() => {
  const contentText = content.value || '';
  if (agentNamePrefix.value) {
    return contentText.replace(/^\*\*[^*]+:\*\*\n/, '');
  }
  return contentText;
});

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return contentWithoutPrefix.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return contentWithoutPrefix.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const googleMapsLocation = computed(() => {
  const text = renderContent.value || '';
  const match = text.match(
    /https?:\/\/(?:www\.)?(?:maps\.google\.com|google\.com\/maps)\/?\?q=(-?\d+\.\d+),(-?\d+\.\d+)/
  );
  if (!match) return null;
  return {
    url: match[0],
    lat: parseFloat(match[1]),
    lng: parseFloat(match[2]),
  };
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <FacebookPostContext :content-attributes="contentAttributes" />
      <div v-if="agentNamePrefix" class="font-bold text-sm -mb-1">
        {{ agentNamePrefix }}:
      </div>
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <PhoneNumberFormattedContent
        v-if="renderContent"
        :content="renderContent"
      />
      <LocationMap
        v-if="googleMapsLocation"
        :lat="googleMapsLocation.lat"
        :lng="googleMapsLocation.lng"
        :link="googleMapsLocation.url"
      />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
